<template>
	<div class="mini-list">
		<div v-for="(item, i) in dataList" :key="i" class="mini-list-item">
			<NuxtLink :to="detailHref(item)" class="product-link" @click="onProductClick(item)">
				<NuxtImg
					:src="getImageUrl(item)"
					class="product-image"
					:alt="getImageAlt(item)"
					:style="{ borderRadius: '5px', width: '100%', height: '175.82px' }"
					loading="lazy"
					format="webp"
					@error="handleImageError"
				/>
				<div class="link-detail">
					<ExpandableText customClass="title-content" :enableButton="false" :dt="item.productName || item.productname" />
					<div class="goods-info">
						<ExpandableText
							v-if="item.specifications || item.optionStr"
							:enableButton="false"
							:dt="`规格：${item.specifications || item.optionStr}`"
						/>
						<ExpandableText v-if="item.manufacturer" :enableButton="false" :dt="item.manufacturer" />
					</div>
				</div>
			</NuxtLink>
			<div class="goods-detail">
				<!-- Price and Cart Button -->
				<div class="goods-btn">
					<ShowAmount :price="formatItemPrice(item)" />

					<!-- Add to Cart Icon -->
					<div
						class="cart-icon-container"
						:class="{ disabled: isCartIconDisabled(item) }"
						@click.stop="!isCartIconDisabled(item) && handleAddCart($event, item)"
					>
						<i class="iconfont icon-plus-circle-fill cart-icon" :style="{ color: isCartIconDisabled(item) ? '#c9cdd4' : '#798bf2' }"></i>
					</div>
				</div>

				<!-- Site Distribution -->
				<div v-if="showSite && item.companyId" class="goods-site" :data-company-id="item.companyId" @click.stop.prevent="goChildSite(item)">
					<div class="site-text">站点经销</div>
					<div class="site-name">{{ item.websiteName }}</div>
				</div>

				<!-- Tags -->
				<div class="goods-icon">
					<!-- Coupon -->
					<van-tag v-if="item.coupon" color="#F24949" text-color="#fff" style="height: 1.33rem">
						{{ item.coupon.maxMoneyStr }}
					</van-tag>

					<!-- Special Price -->
					<van-tag v-if="item.activityType == 1" color="#ff3434" text-color="#fff" style="height: 1.33rem">特</van-tag>

					<!-- Discount -->
					<van-tag v-if="item.activityType == 2" color="#7F90F5" text-color="#fff" style="height: 1.33rem">折</van-tag>

					<!-- Reduction -->
					<van-tag v-if="item.activityType == 3" color="#0EAB32" text-color="#fff" style="height: 1.33rem">减</van-tag>

					<!-- Buy Gift -->
					<van-tag v-if="item.zero" color="#ff9045" text-color="#fff" style="height: 1.33rem">买赠</van-tag>

					<!-- Hot Selling -->
					<van-tag v-if="item.tagList && item.tagList.indexOf('1') != -1" color="#f05d45" text-color="#fff" style="height: 1.33rem">
						热销
					</van-tag>

					<!-- Essential -->
					<van-tag v-if="item.tagList && item.tagList.indexOf('5') != -1" color="#46539d" text-color="#fff" style="height: 1.33rem">
						必备
					</van-tag>

					<!-- Near Expiry -->
					<van-tag v-if="item.tagList && item.tagList.indexOf('3') != -1" color="#46acdf" text-color="#fff" style="height: 1.33rem">
						临期
					</van-tag>

					<!-- Selected -->
					<van-tag v-if="item.tagList && item.tagList.indexOf('2') != -1" color="#5ab355" text-color="#fff" style="height: 1.33rem">
						精选
					</van-tag>
				</div>
			</div>
		</div>

		<!-- 购物车组件：只有登录且 showCart 为 true 时才显示 -->
		<div class="footer-area" v-if="showCart">
			<ShoppingCart ref="shoppingCartRef" />
		</div>
	</div>
</template>

<script setup>
import { ref } from 'vue';
import { addCart } from '@/api/goods';
import { mobileProductDetailPath } from '@/utils/link/mobileProductDetail';
import { getCooperationListPriceLabel, getPrice } from '@/utils/productPrice';
import { showToast } from 'vant';
import ExpandableText from '@/components/ExpandableText.vue';
import ShoppingCart from '@/components/core/ShoppingCart.vue';
import ShowAmount from '@/components/ShowAmount.vue';
import { useAnalytics } from '@/composables/useAnalytics';

const { formatPrice } = usePriceFormat();
const { trackEvent } = useAnalytics();
const route = useRoute();

// Props
const props = defineProps({
	showCart: {
		type: Boolean,
		default: false,
	},
	dataList: {
		type: Array,
		default: () => [],
	},
	automaticallyAddToCart: {
		type: Boolean,
		default: true,
	},
	showSite: {
		type: Boolean,
		default: true,
	},
	/** 来源页面，用于加购埋点 attrName：如「总站首页」「运营商品位」 */
	fromPage: {
		type: String,
		default: '',
	},
});

// Emits
const emit = defineEmits(['addCart', 'click']);

// Refs
const shoppingCartRef = ref(null);

const getImageAlt = item => {
	const brand = item.seoBrand || item.brandName || '';
	const productName = item.productName || item.productname || '';
	const spec = item.specifications || item.optionStr || '';

	// alt={品牌} {商品标题} {规格}
	const parts = [brand, productName, spec].filter(Boolean);
	return parts.join(' ') || '商品图片';
};

const handleImageError = e => {
	e.target.src = '/staticSeo/images/placeholder.png';
};

const getImageUrl = item => {
	return item.defaultImageUrl || item.imageUrl || item.defaultimageurl || '/staticSeo/images/placeholder.png';
};

/** 列表展示价：合作价固定文案；否则参考价 + 控销走 formatPrice（与 ShowAmount 一致） */
const formatItemPrice = item => {
	const coop = getCooperationListPriceLabel(item);
	if (coop != null) return coop;
	const itemPrice = getPrice(item);
	const controlSales = item.isControlSales ?? item.iscontrolsales;
	const isControlSalesProduct = controlSales !== undefined && controlSales !== null && controlSales !== '' && Number(controlSales) === 1;
	if (!isControlSalesProduct) {
		return formatPrice(itemPrice, 2);
	}
	const isSeeNoBuy = item.isSeeNoBuy ?? item.isseenobuy;
	return formatPrice(itemPrice, 2, 1, isSeeNoBuy);
};

/** 合作价文案可加购；其余与原先 `isNaN(formatItemPrice)` 一致（可询价等不可点） */
const isCartIconDisabled = item => {
	if (getCooperationListPriceLabel(item) != null) return false;
	return Number.isNaN(Number(formatItemPrice(item)));
};

// 详情页链接（源码可见，SEO）
const detailHref = item => mobileProductDetailPath(item.companyId, item.productId) || '#';

// Navigation Methods
const goChildSite = item => {
	navigateTo(`/${item.companyId}`);
};

function buildClickPayload(item) {
	return {
		productCode: item.productCode || item.productcode,
		productName: item.productName || item.productname,
		companyCode: item.filialeCode || item.filialecode || item.companyCode || item.companycode,
		companyName: item.filialeName || item.filialename || item.companyName || item.companyname,
	};
}

function onProductClick(item) {
	emit('click', buildClickPayload(item));
}

const handleAddCart = async (event, item) => {
	const options = {
		productId: item.productId,
		price: getPrice(item),
		companyId: item.mobileCompanyId || item.companyId,
		quantity: 1,
		agreementPriceId: item.agreementPriceId,
		filialeCode: item.filialeCode || item.filialecode,
		filialeName: item.filialeName || item.filialename,
		productCode: item.productCode || item.productcode,
		productName: item.productName || item.productname,
		displayPrice: formatItemPrice(item),
	};

	if (props.fromPage) {
		trackEvent({
			companyCode: options.filialeCode,
			companyName: options.filialeName,
			module: '购物车',
			eventCode: 'add_to_cart',
			eventName: '加入购物车',
			attrCode: 'channel_type',
			attrName: props.fromPage,
			ext1: options.productCode,
			ext2: options.productName,
			ext3: options.displayPrice,
		});
	}

	if (!props.automaticallyAddToCart) {
		emit('addCart', event, options);
		return;
	}

	try {
		const params = {
			currentCompanyId: options.companyId,
			productId: options.productId,
			quantity: options.quantity,
			agreementPriceId: options.agreementPriceId,
			price: options.price,
			filialeCode: options.filialeCode,
		};

		const response = await addCart(params);

		if (response.code == 200) {
			showToast({
				message: '加入购物车成功',
				type: 'success',
				duration: 2000,
			});

			if (props.showCart && shoppingCartRef.value) {
				shoppingCartRef.value.addCart(event);
			}
		}
	} catch (error) {
		console.error('添加购物车失败:', error);
	}
};
</script>

<style scoped lang="scss">
.footer-area {
	position: fixed;
	bottom: 0;
	width: 100%;
	background: #f3f4f6;
	z-index: 100;
}

.title-content {
	width: 100%;
	font-family: Source Han Sans;
	font-size: 1.1rem;
	font-weight: bold;
	line-height: 1.5rem;
	color: #333333;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.mini-list {
	display: flex;
	flex-wrap: wrap;
	row-gap: 8px;
	column-gap: 8px;
	margin-top: 8px;
	margin-bottom: 8px;
	padding: 0 8px;

	.mini-list-item {
		flex: 0 0 calc(50% - 4px);
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		background: #ffffff;
		border-radius: 5px;
		overflow: hidden;
		cursor: pointer;

		.product-link {
			display: block;
			text-decoration: none;
			color: inherit;
		}

		.product-image {
			width: 100%;
			height: 175.82px;
			object-fit: cover;
		}

		.link-detail {
			padding: 4px 8px 0;
		}

		.goods-detail {
			flex: 1;
			display: flex;
			flex-direction: column;
			width: 100%;
			padding: 0 8px 8px;

			.goods-btn {
				margin-top: auto;
				width: 100%;
				display: flex;
				gap: 0.42rem;
				height: 24px;
				justify-content: space-between;
				align-items: center;

				.cart-icon-container {
					width: 24px;
					height: 24px;
					display: flex;
					align-items: center;
					justify-content: center;
					cursor: pointer;
					border-radius: 50%;

					&.disabled {
						cursor: not-allowed;
					}

					&:not(.disabled):active {
						background: rgba(121, 139, 242, 0.2);
					}
				}
			}

			.goods-info {
				font-family: Source Han Sans;
				font-size: 0.83rem;
				font-weight: normal;
				line-height: 1.17rem;
				color: #999999;

				:deep(.expandable-text-container) {
					width: 100%;
					margin: 2px 0;
				}
			}

			.goods-icon {
				padding-top: 0.25rem;
				width: 100%;
				display: flex;
				gap: 0.17rem;
				align-items: center;
				flex-wrap: wrap;
			}

			.goods-site {
				opacity: 1;
				display: flex;
				flex-direction: row;
				align-items: center;
				height: 16px;
				cursor: pointer;
				text-decoration: none;
				color: inherit;

				.site-text {
					border: 1px solid #e5ebff;
					width: 48px;
					line-height: 14px;
					border-radius: 2px 0px 0px 2px;
					background: #e5ecff;
					font-size: 10px;
					color: #5f6fc9;
					text-align: center;
				}

				.site-name {
					border: 1px solid #e5ebff;
					line-height: 14px;
					border-radius: 0 2px 2px 0;
					font-size: 10px;
					font-weight: normal;
					text-align: center;
					padding: 0 4px;
					color: #46539d;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
				}
			}
		}
	}
}
</style>
